import { useMemo } from "react";

/* ───────── helpers PUROS (fuera del hook) ───────── */
const monthsAbbrev = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];
const monthsFull = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/* ----- formato de fechas ----- */
function formatDate(dateString, returnFormats = false) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return returnFormats ? [""] : "";

  const day = date.getDate().toString().padStart(2, "0");
  const dayNoPad = date.getDate().toString();
  const monthAbbrev = monthsAbbrev[date.getMonth()];
  const monthFull = monthsFull[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);

  const formatted = `${day} ${monthAbbrev} ${year}`;

  if (!returnFormats) return formatted;
  return [
    formatted.toLowerCase(),
    `${dayNoPad} ${monthFull}`.toLowerCase(),
    `${dayNoPad}, ${monthFull}`.toLowerCase(),
    `${dayNoPad} ${monthFull} ${year}`.toLowerCase(),
    `${day} ${monthFull}`.toLowerCase(),
    monthFull,
    monthAbbrev.toLowerCase(),
  ];
}

/* ----- formato de moneda ----- */
function formatCurrency(num = 0) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/* ----- normalización de números ----- */
const trimLeadingZeros = (v = "") =>
  typeof v === "string" ? v.replace(/^0+(?=\d)/, "") : v;

/* ------------------------------------------------------------------ */
/* ---------- MÉTRICAS Y DASHBOARD ---------- */

/** Sub-, IVA y total de un arreglo de productos */
function calcTotals(rows = []) {
  const subtotal = rows.reduce(
    (s, { cantidad = 0, precioUnitario = 0 }) => s + cantidad * precioUnitario,
    0
  );
  const iva = subtotal * 0.16;
  return { subtotal, iva, total: subtotal + iva };
}

/** Datos agrupados por carpeta para el pie-chart */
function calcPieData(rows = []) {
  const byFolder = rows.reduce(
    (acc, { listaNombre, cantidad = 0, precioUnitario = 0 }) => {
      const key = listaNombre?.trim() || "Sin carpeta";
      acc[key] = (acc[key] || 0) + cantidad * precioUnitario;
      return acc;
    },
    {}
  );
  return Object.entries(byFolder).map(([name, value]) => ({ name, value }));
}

/** Métricas globales del dashboard (ingresos, gastos, balance, total) */
function calcDashboard(rows = [], listas = []) {
  // Cada carpeta puede tener opcionalmente `tipo: "ingreso" | "gasto"`
  const tipoCarpeta = Object.fromEntries(
    listas.map((l) => [l.id, l.tipo || "gasto"])
  );

  let ingresos = 0;
  let gastos = 0;

  rows.forEach(({ listaId, cantidad = 0, precioUnitario = 0 }) => {
    const monto = cantidad * precioUnitario;
    if (!monto) return;

    if (tipoCarpeta[listaId] === "ingreso") ingresos += monto;
    else gastos += monto;
  });

  // <-- aquí agregamos el cálculo del IVA y el total con IVA
  const subtotal = ingresos + gastos;
  const iva = subtotal * 0.16;
  const totalConIVA = subtotal + iva;

  return {
    totalCarpetas: totalConIVA, // ahora incluye IVA
    ingresos,
    gastos,
    balance: ingresos - gastos,
  };
}

/* ------------------------------------------------------------------ */
/* ---------- HOOK ---------- */
export function useFunctionsPrevia() {
  return useMemo(
    () => ({
      formatDate,
      formatCurrency,
      trimLeadingZeros,
      calcTotals,
      calcPieData,
      calcDashboard,
    }),
    []
  );
}
